import { DEFAULT_REGISTRY_SECTION_NUMS, type RegistrySectionNums } from '../../utils/sopSectionNumbering';

const cleanSOPContent = (content: string): string => {
  if (!content) return '';
  
  let cleaned = content;
  
  // Remove source citation fragments that interrupt readability
  cleaned = cleaned.replace(/●\s*\[SOURCE:\s*\][^●\n]*/g, '');
  // Preserve transparency markers while cleaning other citations
  cleaned = cleaned.replace(/\[SOURCE:[^\]]*\](?!\s*-)/g, ''); // Remove generic SOURCE markers but keep transparency ones
  cleaned = cleaned.replace(/\[Based on:[^\]]*\]/g, '');
  
  // Clean up excessive whitespace
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  
  // Ensure proper paragraph breaks
  cleaned = cleaned.replace(/\n\s*\n/g, '\n\n');
  
  return cleaned;
};

// NEW: Function to render text with inline markers and bold text
const renderTextWithInlineMarkers = (text: string, registryNums: RegistrySectionNums = DEFAULT_REGISTRY_SECTION_NUMS) => {
  // Guarantee a space between two adjacent bracket tags ("...]​[..." → "...] [...")
  // so consecutive marker chips (e.g. two GAP references back-to-back with no
  // whitespace in the source) don't render visually touching each other.
  const spacedText = text.replace(/\]\[/g, '] [');

  // Split text by ALL markers while preserving them
  // Updated regex to handle optional spaces after colon and match until ] or end of sentence
  // Added PRESERVED and ADDITION markers for dual-mode processing transparency
  // Added "(applies to: ...)" — a plain-parens clause (not a [bracket] tag) that follows
  // a [GAP IDENTIFIED: ...] marker in Section 12.0, e.g. "G-01: [GAP IDENTIFIED: ...]
  // (applies to: Section 2.0, Section 4.0) Recommended Owner: ...". Isolated into its own
  // part so it can render as a centered, smaller, italic line under the gap box instead of
  // flowing inline with the surrounding sentence.
  const parts = spacedText.split(/(\[(?:DOCUMENTED|INFERRED|ASSUMED|CONTROL POINT|GAP IDENTIFIED|SCREENSHOT NEEDED|FLOWCHART NEEDED|CONFLICT|SOURCE|ASSUMPTION|PRESERVED|ADDITION|AI ADDITION|ORIGINAL):[^\]]+\]|\[Incorporated from [^\]]+ documentation\]|\[Corrected per [^\]]+ documentation:[^\]]+\]|\[GAP IDENTIFIED: Not found in [^\]]+ documentation[^\]]*\]|\[→\s*[CG]-\d+:[^\]]+\]|\[RECOMMENDATION:[^\]]+\]|\(applies to:[^)]+\))/);

  return parts.map((part, index) => {
    // Check if this part is a PRESERVED/ORIGINAL marker (content from original document)
    if (part.match(/^\[(?:PRESERVED|ORIGINAL):[^\]]*\]$/)) {
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];
      return (
        <span key={index} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 mx-0.5 whitespace-nowrap align-baseline">
          <span className="text-xs">✓</span>
          <span className="font-medium">Original:</span>
          <span className="font-normal">{markerContent}</span>
        </span>
      );
    }

    // Grouped Control/Gap cross-references ("[→ C-14: ...] [→ G-08: ...]") — these
    // point back to the full entry already registered in Section 8.0 (Controls) or
    // Section 12.0 (Gaps). Previously each tag rendered as its own gray italic span,
    // so a Control ref and a Gap ref sitting next to each other ran together on one
    // line. Collect the whole consecutive run (tags separated only by whitespace) and
    // render Controls and Gaps on their own color-coded lines — dark green for
    // Controls, maroon for Gaps, matching their respective sections (Asana
    // 1216992325540149, item 3).
    const XREF_RE = /^\[→\s*([CG])-(\d+):\s*([^\]]+)\]$/;
    if (XREF_RE.test(part)) {
      // Skip if the previous non-whitespace part was also an xref — it already
      // rendered this whole run.
      let prevIdx = index - 1;
      while (prevIdx >= 0 && /^\s*$/.test(parts[prevIdx])) prevIdx--;
      if (prevIdx >= 0 && XREF_RE.test(parts[prevIdx])) return null;

      // Walk forward collecting every xref in this run.
      const refs: { kind: 'C' | 'G'; num: string; label: string }[] = [];
      let i = index;
      while (i < parts.length) {
        if (i !== index && /^\s*$/.test(parts[i])) { i++; continue; }
        const m = parts[i].match(XREF_RE);
        if (!m) break;
        refs.push({ kind: m[1] as 'C' | 'G', num: m[2], label: m[3] });
        i++;
      }
      const controls = refs.filter((r) => r.kind === 'C');
      const gaps = refs.filter((r) => r.kind === 'G');
      const fmt = (list: typeof refs) => list.map((r) => `${r.kind}-${r.num} (${r.label})`).join(', ');
      return (
        <span key={index} className="block mt-1 space-y-0.5">
          {controls.length > 0 && (
            <span className="block text-xs italic text-green-800">
              Control{controls.length > 1 ? 's' : ''} {fmt(controls)}{registryNums.controls ? ` — see section ${registryNums.controls}` : ''}
            </span>
          )}
          {gaps.length > 0 && (
            <span className="block text-xs italic text-[#800000]">
              Gap{gaps.length > 1 ? 's' : ''} {fmt(gaps)}{registryNums.gaps ? ` — see section ${registryNums.gaps}` : ''}
            </span>
          )}
        </span>
      );
    }

    // Check if this part is a recommended (not confirmed) policy reference —
    // used in Section 11.0 alongside [SOURCE:]-cited confirmed references.
    // Bold+italic label at the same size as the description text that follows
    // it (no text-xs override — inherits the bullet's default size), with a
    // dash separating the label from the description.
    if (part.match(/^\[RECOMMENDATION:[^\]]*\]$/)) {
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];
      return [
        <strong key={`${index}-label`} className="italic font-semibold text-gray-700">Recommended: {markerContent}</strong>,
        ' — ',
      ];
    }

    // Check if this part is an ADDITION/AI ADDITION marker (AI-added content)
    if (part.match(/^\[(?:ADDITION|AI ADDITION):[^\]]*\]$/)) {
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];
      return (
        <span key={index} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200 mx-0.5 whitespace-nowrap align-baseline">
          <span className="text-xs">+</span>
          <span className="font-medium">AI Added:</span>
          <span className="font-normal">{markerContent}</span>
        </span>
      );
    }

    // Check if this part is a Workday/tool documentation marker
    if (part.match(/^\[Incorporated from .+ documentation\]$/) || part.match(/^\[Corrected per .+ documentation:[^\]]*\]$/) || part.match(/^\[GAP IDENTIFIED: Not found in .+ documentation[^\]]*\]$/)) {
      const isIncorporated = part.match(/^\[Incorporated from (.+) documentation\]$/);
      const isCorrected = part.match(/^\[Corrected per (.+) documentation:([^\]]*)\]$/);
      const isGap = part.match(/^\[GAP IDENTIFIED: Not found in (.+) documentation/);
      const platform = (isIncorporated?.[1] || isCorrected?.[1] || isGap?.[1] || 'Workday');
      if (isIncorporated) {
        return (
          <span key={index} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 mx-0.5 whitespace-nowrap align-baseline">
            <span>📋</span>
            <span>Incorporated from {platform} documentation</span>
          </span>
        );
      }
      if (isCorrected) {
        return (
          <span key={index} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-cyan-50 text-cyan-700 border border-cyan-200 mx-0.5 align-baseline">
            <span>✏️</span>
            <span>Corrected per {platform} documentation:{isCorrected[2]}</span>
          </span>
        );
      }
      if (isGap) {
        return (
          <span key={index} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-200 mx-0.5 align-baseline">
            <span>⚠️</span>
            <span>Not found in {platform} documentation — requires SME verification</span>
          </span>
        );
      }
    }

    // Check if this part is a transparency marker
    if (part.match(/^\[(?:DOCUMENTED|INFERRED|ASSUMED):[^\]]*\]$/)) {
      const markerType = part.match(/^\[(\w+):/)?.[1];
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];

      let bgColor = '';
      let textColor = '';
      let borderColor = '';
      let icon = '';
      let label = '';

      switch (markerType) {
        case 'DOCUMENTED':
          bgColor = 'bg-green-50';
          textColor = 'text-green-700';
          borderColor = 'border-green-200';
          icon = '✓';
          label = 'Original';
          break;
        case 'INFERRED':
          bgColor = 'bg-violet-100';
          textColor = 'text-violet-800';
          borderColor = 'border-violet-200';
          icon = '+';
          label = 'AI Added';
          break;
        case 'ASSUMED':
          bgColor = 'bg-amber-100';
          textColor = 'text-amber-800';
          borderColor = 'border-amber-200';
          icon = '?';
          label = 'AI Assumed';
          break;
      }

      return (
        <span key={index} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${bgColor} ${textColor} border ${borderColor} mx-0.5 whitespace-nowrap align-baseline`}>
          <span className="text-xs font-bold">{icon}</span>
          <span className="font-medium">{label}:</span>
          <span className="font-normal">{markerContent}</span>
        </span>
      );
    }

    // Check if this part is a CONTROL POINT marker (inline)
    if (part.match(/^\[CONTROL POINT:[^\]]*\]$/)) {
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];
      return (
        <span key={index} className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium bg-yellow-50 text-yellow-800 mx-1 border border-yellow-200">
          <span className="text-sm">🔍</span>
          <span className="font-semibold">CONTROL POINT:</span>
          <span>{markerContent}</span>
        </span>
      );
    }

    // Check if this part is a GAP IDENTIFIED marker. Forced to the SAME block/box
    // treatment as the standalone-line version further down, regardless of whether
    // the tag landed mid-sentence or alone on its own line, so gap callouts never
    // look different depending on incidental sentence position (Asana
    // 1216992325540149, item 4: "gaps rendered inconsistently").
    if (part.match(/^\[GAP IDENTIFIED:[^\]]*\]$/)) {
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];
      return (
        <span key={index} className="block bg-red-50 border-l-4 border-red-400 p-3 my-2 rounded-r-md">
          <span className="flex items-center gap-2">
            <span className="text-lg">⚠️</span>
            <span className="text-red-800 font-medium">Gap: {markerContent}</span>
          </span>
        </span>
      );
    }

    // "(applies to: ...)" clause following a gap marker — rendered as its own
    // centered, italic, one-point-smaller line directly under the gap box
    // (block display forces it onto its own line rather than flowing inline
    // with the "Recommended Owner:"/"Severity:" text that follows it).
    if (part.match(/^\(applies to:[^)]+\)$/)) {
      return (
        <span key={index} className="block text-center italic text-[13px] text-gray-500 my-1">
          {part}
        </span>
      );
    }

    // Check if this part is a SCREENSHOT NEEDED marker (inline)
    if (part.match(/^\[SCREENSHOT NEEDED:[^\]]*\]$/)) {
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];
      return (
        <span key={index} className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium bg-blue-50 text-blue-800 mx-1 border border-blue-200">
          <span className="text-sm">📸</span>
          <span className="font-semibold">SCREENSHOT:</span>
          <span>{markerContent}</span>
        </span>
      );
    }

    // Check if this part is a FLOWCHART NEEDED marker (inline)
    if (part.match(/^\[FLOWCHART NEEDED:[^\]]*\]$/)) {
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];
      return (
        <span key={index} className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium bg-green-50 text-green-800 mx-1 border border-green-200">
          <span className="text-sm">📊</span>
          <span className="font-semibold">FLOWCHART:</span>
          <span>{markerContent}</span>
        </span>
      );
    }

    // Check if this part is a CONFLICT marker (inline)
    if (part.match(/^\[CONFLICT:[^\]]*\]$/)) {
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];
      return (
        <span key={index} className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium bg-orange-50 text-orange-800 mx-1 border border-orange-200">
          <span className="text-sm">⚡</span>
          <span className="font-semibold">CONFLICT:</span>
          <span>{markerContent}</span>
        </span>
      );
    }

    // Check if this part is an ASSUMPTION marker (inline)
    if (part.match(/^\[ASSUMPTION:[^\]]*\]$/)) {
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];
      return (
        <span key={index} className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium bg-purple-50 text-purple-800 mx-1 border border-purple-200">
          <span className="text-sm">💡</span>
          <span className="font-semibold">ASSUMPTION:</span>
          <span>{markerContent}</span>
        </span>
      );
    }

    // SOURCE marker (inline) — previously rendered as a "✓ Original: <filename>"
    // green badge, but the filename it names is already stated earlier in the
    // same Section 11.1 bullet line, making the badge a redundant repeat.
    // Render nothing; the citation still exists in the underlying data, it's
    // just no longer duplicated visually.
    if (part.match(/^\[SOURCE:[^\]]*\]$/)) {
      return null;
    }

    // Handle bold text (**text**) and remove orphan **
    if (part.includes('**')) {
      // Remove orphan ** (not paired properly)
      let cleanedPart = part.replace(/\*\*/g, '');

      // Now try to find proper bold pairs
      const boldMatches = part.match(/\*\*(.+?)\*\*/g);
      if (boldMatches && boldMatches.length > 0) {
        // Has proper bold pairs, split and render
        const boldParts = part.split(/(\*\*[^*]+?\*\*)/);
        return boldParts.map((boldPart, boldIndex) => {
          if (boldPart.match(/^\*\*.+?\*\*$/)) {
            const boldText = boldPart.replace(/\*\*/g, '').trim();
            if (boldText) {
              return <strong key={`${index}-${boldIndex}`} className="font-semibold">{boldText}</strong>;
            }
          }
          return boldPart;
        });
      }
      // No proper pairs, just return cleaned part without **
      return cleanedPart;
    }

    // Style any referenced URLs as smaller italic reference text, inline with the
    // surrounding sentence rather than the default paragraph size/weight.
    if (/https?:\/\/\S+/.test(part)) {
      const urlSplit = part.split(/(https?:\/\/\S+)/);
      return urlSplit.map((piece, urlIndex) =>
        /^https?:\/\/\S+$/.test(piece)
          ? <span key={`${index}-url-${urlIndex}`} className="text-xs italic text-gray-500 break-all">{piece.replace(/[.,;)]+$/, '')}</span>
          : piece
      );
    }

    // Bold a leading document filename before an em-dash, e.g. Section 11.1's
    // "Employee_Relations_Investigations_06082026.pdf — Primary source
    // document for this SOP." Real uploaded filenames routinely contain
    // spaces, parentheses, apostrophes, and ampersands (e.g. "Payroll US -
    // Subpoena Requests 06082026.pdf") — the old character class only
    // allowed [A-Za-z0-9_.-], so any filename with a space silently fell
    // through to plain text and never bolded. Match non-greedily up to the
    // LAST run ending in a known extension right before the em-dash instead,
    // so any filename shape bolds consistently while ordinary prose that
    // merely mentions "pdf" mid-sentence still won't match (it won't end in
    // ".ext" immediately before " — ").
    const filenameMatch = part.match(/^([\s\S]+?\.(?:pdf|docx?|xlsx?|pptx?|csv|txt))(\s+—\s+)([\s\S]*)$/i);
    if (filenameMatch) {
      const [, filename, dash, rest] = filenameMatch;
      return [
        <strong key={`${index}-filename`} className="font-semibold">{filename}</strong>,
        dash,
        rest,
      ];
    }

    // Bold the "Appendix X — Title" prefix in Section 12.0. Two real shapes seen:
    // title-case with the description continuing after a colon on the same line
    // ("Appendix A — Process Flow Summary: The ER Investigations process...")
    // and all-caps with no colon, description as a separate paragraph below
    // ("APPENDIX A — SPERT-7 RULE QUICK REFERENCE"). Case-insensitive match;
    // colon-and-rest is optional so the whole line bolds when there's no colon.
    const appendixMatch = part.match(/^(Appendix\s+[A-Z]\s+—\s+[^:]+:?)([\s\S]*)$/i);
    if (appendixMatch) {
      const [, heading, rest] = appendixMatch;
      return [
        <strong key={`${index}-appendix`} className="font-semibold">{heading}</strong>,
        rest,
      ];
    }

    // Regular text - trim leading/trailing spaces to prevent spacing issues
    return part.trim();
  });
};

/**
 * Section 11's reference sub-headings. The number is optional and the text may
 * be upper- or title-case: the generator writes this heading as "11.1 Confirmed
 * References" in some SOPs and as a bare "CONFIRMED REFERENCES" in others, and
 * both shapes have to be recognised or the entries under them are treated as
 * ordinary prose.
 */
const REFERENCE_HEADING_RE = /^\**\s*(?:\d+(?:\.\d+)+\s+)?(confirmed|recommended)\s+references\b/i;

/** A multi-level section/step header line, e.g. "11.0 ...", "5.1 ...", "5.1.2 ...". */
const NUMBERED_HEADING_RE = /^\d+(?:\.\d+)+\s+.+$/;

/**
 * Which reference list the line at `index` sits in, or null if it sits in none.
 * Scans backward to the nearest heading of any kind and checks which one it is,
 * so 11.1 Confirmed and 11.2 Recommended stay distinguishable from each other
 * and from every other section.
 */
const referenceListKind = (lines: string[], index: number): 'confirmed' | 'recommended' | null => {
  for (let i = index - 1; i >= 0; i--) {
    const priorLine = lines[i].trim();
    const referenceHeading = priorLine.match(REFERENCE_HEADING_RE);
    if (referenceHeading) {
      return referenceHeading[1].toLowerCase() === 'confirmed' ? 'confirmed' : 'recommended';
    }
    if (NUMBERED_HEADING_RE.test(priorLine)) return null;
  }
  return null;
};

/**
 * Renders one Confirmed References entry with its document/policy name bold.
 * Entries arrive as "<Name> — <description>" or, when the model omits the
 * description, as "<Name> [SOURCE: ...]". The name itself is anything: an
 * uploaded filename carrying spaces, parentheses, apostrophes and ampersands
 * ("Payroll US - Subpoena Requests 06082026.pdf"), or a plain policy title with
 * no file extension at all ("IT Access Request Procedure"). Only the em-dash is
 * accepted as the separator — a hyphen would split filenames that contain one.
 * With neither separator present the line falls back to normal rendering, so a
 * [GAP IDENTIFIED: ...] placeholder standing in for the list never gets bolded.
 */
const renderReferenceEntry = (text: string, registryNums: RegistrySectionNums = DEFAULT_REGISTRY_SECTION_NUMS): React.ReactNode => {
  const dashSplit = text.match(/^([^—]{1,120})(\s+—\s+)([\s\S]*)$/);
  if (dashSplit) {
    return (
      <>
        <strong className="font-semibold">{dashSplit[1].trim()}</strong>
        {dashSplit[2]}
        {renderTextWithInlineMarkers(dashSplit[3], registryNums)}
      </>
    );
  }

  const citationSplit = text.match(/^([^[]{1,120}?)\s*(\[SOURCE:[\s\S]*)$/i);
  if (citationSplit) {
    return (
      <>
        <strong className="font-semibold">{citationSplit[1].trim()}</strong>
        {renderTextWithInlineMarkers(` ${citationSplit[2]}`, registryNums)}
      </>
    );
  }

  return renderTextWithInlineMarkers(text, registryNums);
};

// Semicolon-list-to-bullets: a paragraph (or table cell) containing 4+ semicolon-
// separated items reads as a wall of text and is hard to scan. Detects the pattern
// and returns the parsed { label, introText, items } or null if it doesn't qualify —
// callers render null as their normal single-paragraph output. Shared by both the
// plain-paragraph renderer and the Roles/Definitions table-cell renderer so a role's
// "Description" cell gets the same bullet treatment as a standalone paragraph would.
const parseSemicolonList = (text: string): { label: string | null; intro: string | null; items: string[]; trailing: string | null } | null => {
  const semicolonCount = (text.match(/;/g) || []).length;
  if (semicolonCount < 3 || text.startsWith('[')) return null; // 4+ items = 3+ separators

  // Optional bold label before the first colon becomes the list header (e.g.
  // "Input Required:", "Compensation COE:"). Capped at a short label length so a
  // long lead-in sentence ending in "...including:" isn't mistaken for a field
  // label — that stays as a plain (non-bold) intro line instead.
  const labelMatch = text.match(/^([A-Z][A-Za-z0-9 /&\-]{1,40}):\s*(.*)$/);
  const label = labelMatch ? labelMatch[1] : null;
  const introMatch = !labelMatch && /:\s*\S/.test(text) ? text.match(/^(.*?:)\s*(.*)$/) : null;
  const body = labelMatch ? labelMatch[2] : (introMatch ? introMatch[2] : text);

  const rawSegments = body.split(';').map(s => s.trim()).filter(Boolean);
  if (rawSegments.length < 4) return null;

  // The last semicolon segment often runs "final item. Trailing sentence one.
  // Trailing sentence two." with no semicolon between the list's last item and
  // whatever prose follows it — only a period. Split that segment at its first
  // sentence-ending period so trailing commentary isn't swallowed into the last
  // bullet; keep it as separate prose rendered after the list instead.
  const lastSegment = rawSegments[rawSegments.length - 1];
  const sentenceSplit = lastSegment.match(/^(.+?[.!?])\s+(.+)$/);
  const finalItem = sentenceSplit ? sentenceSplit[1] : lastSegment.replace(/\.\s*$/, '');
  const trailing = sentenceSplit ? sentenceSplit[2].trim() : null;

  const items = [...rawSegments.slice(0, -1), finalItem.replace(/\.\s*$/, '')];
  return { label, intro: introMatch ? introMatch[1] : null, items, trailing };
};

type ViewMode = 'all' | 'original' | 'additions';

// Filter content based on view mode
const filterContentByViewMode = (content: string, viewMode: ViewMode): string => {
  if (viewMode === 'all') return content;

  // Original content markers (preserve these in 'original' mode)
  const originalMarkersRegex = /\[(DOCUMENTED|SOURCE|PRESERVED|ORIGINAL):[^\]]+\]/g;
  // AI-added content markers (preserve these in 'additions' mode)
  const additionMarkersRegex = /\[(INFERRED|ASSUMED|ADDITION|AI ADDITION):[^\]]+\]/g;

  if (viewMode === 'original') {
    // Keep lines that contain original markers, remove addition markers from display
    const lines = content.split(/\n+/);
    return lines
      .map(line => {
        // Remove addition markers from the line
        return line.replace(additionMarkersRegex, '').trim();
      })
      .filter(line => {
        const hasOriginal = originalMarkersRegex.test(line);
        originalMarkersRegex.lastIndex = 0;
        // Keep if has original markers OR has no markers at all (plain text with content)
        return hasOriginal || line.trim().length > 0;
      })
      .join('\n\n');
  }

  if (viewMode === 'additions') {
    const lines = content.split(/\n+/);
    const additionLines: string[] = [];
    lines.forEach(line => {
      const stripped = line.replace(originalMarkersRegex, '').trim();
      originalMarkersRegex.lastIndex = 0;
      if (stripped.length > 0) {
        additionLines.push(stripped);
      }
    });
    return additionLines.join('\n\n');
  }

  return content;
};

const SOPContentRenderer: React.FC<{ content: string; viewMode?: ViewMode; showMarkers?: boolean; sectionTitle?: string; registryNums?: RegistrySectionNums }> = ({ content, viewMode = 'all', showMarkers = true, sectionTitle = '', registryNums = DEFAULT_REGISTRY_SECTION_NUMS }) => {
    if (!content) return null;

    // Apply view mode filtering
    let filteredContent = filterContentByViewMode(content, viewMode);

    // Bracket-closure sanitiser (mirrors the backend generation/export-time passes) —
    // the on-screen preview reads raw stored content directly, so an unclosed tag like
    // "[→ G-04: SLA timelines undocumented" (no closing "]") would otherwise render as
    // literal broken bracket text instead of the styled marker/reference chip.
    {
      // Pass A: unclosed bracket tags before the next [ on the same line
      filteredContent = filteredContent.replace(
        /(\[(?:→\s*[CG]-\d+|Conforms to[^\]\[]{0,120}):[^\]\[\n]+?)(\s*\[)/g,
        (match, tag, nextOpen) => `${tag.trimEnd()}]${nextOpen}`
      );
      // Pass 0: a gap/control cross-reference that runs directly into the next one
      // with no separating space (e.g. "...ER Tracker tool)G-11:" or "...tool]G-11:")
      // — the model sometimes omits the space between one tag's closing punctuation
      // and the next tag's "G-XX:"/"C-XX:" opener. Insert the missing space so the
      // two don't visually run together (Asana 1216992325540149, item 4).
      filteredContent = filteredContent.replace(/([)\]])([CG]-\d+:)/g, '$1 $2');
      // Pass A2: unclosed cross-reference tag that runs into a new "Label:" field
      // on the same line (e.g. "[→ G-04: SLA timelines undocumented Output: ...").
      // Must run BEFORE Pass B — Pass B's end-of-line "$" match would otherwise
      // swallow the rest of the line (including the next field) before this pass
      // gets a chance to find the closer field-boundary cut point, and running
      // both would leave a duplicate "]" behind. Close right before the new field
      // label, not by word count — a fixed word-count cut can swallow the next
      // field's own label into the tag (see Pass D, the final fallback below).
      filteredContent = filteredContent.replace(
        /\[→\s*[CG]-\d+:\s*[^\]\[\n]*?(?=\s+[A-Z][A-Za-z /&-]{1,40}:\s)/g,
        (fullMatch) => fullMatch.includes(']') ? fullMatch : `${fullMatch}]`
      );
      // Pass B: end-of-line unclosed tags (all known tag types)
      filteredContent = filteredContent.replace(
        /\[(SOURCE|GAP IDENTIFIED|ASSUMPTION|CONTROL POINT|RECOMMENDATION|Conforms to[^\]\[]{0,120}|→\s*[CG]-\d+):[^\]\n]*$/gm,
        (fullMatch) => fullMatch + ']'
      );
      // Pass D: prose-swallowed cross-reference / recommendation tags mid-line
      // with no nearby field boundary — fall back to a word-count cut. Only runs
      // per LINE (not per match) on lines that still have an unclosed "[" after
      // Pass A/A2/B — otherwise a tag Pass B already closed at true end-of-line
      // gets a second "]" inserted mid-string by this pass, orphaning the first one.
      filteredContent = filteredContent
        .split('\n')
        .map(lineText => {
          const opens = (lineText.match(/\[/g) || []).length;
          const closes = (lineText.match(/\]/g) || []).length;
          if (closes >= opens) return lineText; // nothing unclosed on this line
          return lineText.replace(
            /(\[→\s*[CG]-\d+:\s*)((?:\S+\s+){5,})([^\]\[\n]*)/g,
            (fullMatch, prefix, longBody, rest) => {
              if (fullMatch.includes(']')) return fullMatch;
              const words = longBody.trim().split(/\s+/);
              const label = words.slice(0, 4).join(' ');
              const overflow = words.slice(4).join(' ');
              return `${prefix}${label}]${overflow ? ' ' + overflow : ''}${rest}`;
            }
          );
        })
        .join('\n');
    }

    // Strip all markers if showMarkers is false
    if (!showMarkers) {
      filteredContent = filteredContent
        .replace(/__PM_STEP_BADGE__[^\n]*/g, '')
        .replace(/\[(?:DOCUMENTED|INFERRED|ASSUMED|CONTROL POINT|GAP IDENTIFIED|SCREENSHOT NEEDED|FLOWCHART NEEDED|CONFLICT|SOURCE|ASSUMPTION|PRESERVED|ADDITION|AI ADDITION|ORIGINAL):[^\]]*\]/g, '')
        .replace(/\[Incorporated from [^\]]+ documentation\]/g, '')
        .replace(/\[Corrected per [^\]]+ documentation:[^\]]+\]/g, '')
        .replace(/\[GAP IDENTIFIED: Not found in [^\]]+ documentation[^\]]*\]/g, '')
        .replace(/✓ Original:[^\n]*/g, '')
        .replace(/\+ AI Added[^\n]*/g, '')
        .replace(/\n\s*\n\s*\n/g, '\n\n')
        .trim();
    }

    // Key Definitions / Roles & Responsibilities are generated as one em-dash-separated
    // line per entry ("TERM — definition..." / "Name — Title: responsibility...").
    // Render those as a two-column table (label bold, detail normal) instead of plain
    // paragraphs, without touching how any other section renders.
    const isDefinitionsSection = /key definitions|acronyms|glossary/i.test(sectionTitle);
    const isRolesSection = /roles?\s*(and|&)?\s*responsibilit/i.test(sectionTitle);
    if (isDefinitionsSection || isRolesSection) {
      const rawLines = filteredContent
        .replace(/\\n\\n/g, '\n\n')
        .replace(/\\n/g, '\n')
        .split('\n')
        .map(l => l.trim())
        .filter(Boolean);
      // A definition/role line looks like "TERM — detail". An intro sentence or a
      // continuation sentence (no em-dash) is folded into the previous entry's detail
      // rather than counted as a failed row, so multi-sentence definitions don't push
      // the match ratio below the table threshold.
      const rows: { label: string; detail: string }[] = [];
      let sawIntro = false;
      for (const l of rawLines) {
        const m = l.match(/^(.+?)\s+—\s+(.+)$/);
        if (m) {
          rows.push({ label: m[1].replace(/:\s*$/, ''), detail: m[2] });
        } else if (rows.length > 0) {
          // continuation of the previous entry
          rows[rows.length - 1].detail += ' ' + l;
        } else {
          // leading intro sentence(s) before the first entry — ignore for ratio
          sawIntro = true;
        }
      }
      const nonIntroLines = rawLines.length - (sawIntro ? rawLines.findIndex(l => /\s+—\s+/.test(l)) : 0);

      // Switch to table rendering when we recovered a healthy set of entries.
      if (rows.length >= 2 && rows.length >= Math.max(2, nonIntroLines * 0.4)) {
        return (
          <table className="w-full text-sm border-collapse">
            <tbody>
              {rows.map((row, i) => {
                // A role/definition detail that itself contains 4+ semicolon-separated
                // items (e.g. a role with 7 listed responsibilities) gets the same
                // bullet-list treatment as a standalone paragraph would.
                const semicolonList = parseSemicolonList(row.detail);
                return (
                <tr key={i} className="border-b border-gray-100 last:border-0 align-top">
                  <td className="py-2 pr-4 w-1/4 whitespace-nowrap font-semibold text-gray-900">{row.label}</td>
                  <td className="py-2 text-gray-700">
                    {semicolonList ? (
                      <>
                        {semicolonList.intro && <p className="mb-1">{semicolonList.intro}</p>}
                        <ul className="list-none space-y-1">
                          {semicolonList.items.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start gap-2">
                              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0" />
                              <span>{renderTextWithInlineMarkers(item, registryNums)}</span>
                            </li>
                          ))}
                        </ul>
                        {semicolonList.trailing && (
                          <p className="mt-1">{renderTextWithInlineMarkers(semicolonList.trailing, registryNums)}</p>
                        )}
                      </>
                    ) : (
                      renderTextWithInlineMarkers(row.detail, registryNums)
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        );
      }
    }

    // Sections 6.0, 7.0, 8.0, 9.0 are generated as pipe-delimited rows (one entry per
    // line, "field | field | field") per the backend prompt. Render each as a table
    // with column headers matched to the section, instead of raw pipe-separated text.
    const PIPE_TABLE_COLUMNS: Record<string, string[]> = {
      system: ['System', 'Interaction / Navigation', 'Controls / Gaps'],
      exception: ['Component / Scenario', 'Resolution', 'Controls / Gaps'],
      controls: ['Control ID', 'Description', 'Owner', 'Type', 'Frequency'],
      records: ['Record / Item', 'Description', 'Retention Period', 'Controls / Gaps'],
    };
    // Explicit per-column width shares so a long Description cell can't inflate a
    // short-content column (e.g. Control ID) — table-layout:auto sizes columns from
    // cell content, which pushes narrow columns wide when a neighboring cell wraps.
    const PIPE_TABLE_WIDTHS: Record<string, string[]> = {
      system: ['w-1/6', 'w-1/2', 'w-1/3'],
      exception: ['w-1/5', 'w-1/2', 'w-3/10'],
      controls: ['w-[19%]', 'w-2/5', 'w-[10%]', 'w-[8%]', 'w-[10%]'],
      records: ['w-1/5', 'w-2/5', 'w-[15%]', 'w-1/4'],
    };
    let pipeTableKey: string | null = null;
    if (/system interaction|system.*navigation/i.test(sectionTitle)) pipeTableKey = 'system';
    else if (/exception|conflict|interpretation resolution/i.test(sectionTitle)) pipeTableKey = 'exception';
    else if (/quality control|compliance|controls registry/i.test(sectionTitle)) pipeTableKey = 'controls';
    else if (/documentation and record keeping/i.test(sectionTitle)) pipeTableKey = 'records';

    if (pipeTableKey) {
      const columns = PIPE_TABLE_COLUMNS[pipeTableKey];
      const rawLines = filteredContent
        .replace(/\\n\\n/g, '\n\n')
        .replace(/\\n/g, '\n')
        .split('\n')
        .map(l => l.trim())
        .filter(Boolean);

      // The model doesn't always follow the pipe-delimited instruction exactly — it
      // sometimes falls back to "Label — description | ref refs" (em-dash for the first
      // split, pipe only before trailing control/gap refs). Parse both shapes: split on
      // "|" first; if that yields fewer cells than expected columns, also split the first
      // cell on " — " to recover a Label/Description pair before the refs column(s).
      const pipeRows = rawLines
        .filter(l => l.includes('|') || / — /.test(l))
        .map(l => {
          let cells = l.split('|').map(cell => cell.trim());
          if (cells.length < columns.length && cells[0].includes(' — ')) {
            const [first, ...restOfFirst] = cells[0].split(' — ');
            cells = [first.trim(), restOfFirst.join(' — ').trim(), ...cells.slice(1)];
          }
          return cells;
        });

      // Only switch to table rendering if most lines actually parsed into multiple
      // cells — otherwise fall through to the normal renderer (e.g. a
      // [GAP IDENTIFIED: ...] no-entries line, or genuinely unstructured prose).
      const wellFormedRows = pipeRows.filter(cells => cells.length >= 2);
      if (wellFormedRows.length > 0 && wellFormedRows.length >= rawLines.length * 0.6) {
        const widths = PIPE_TABLE_WIDTHS[pipeTableKey];
        return (
          <table className="w-full text-sm border-collapse table-fixed">
            <colgroup>
              {columns.map((col, colIndex) => (
                <col key={col} className={widths[colIndex]} />
              ))}
            </colgroup>
            <thead>
              <tr className="border-b border-gray-200">
                {columns.map(col => (
                  <th key={col} className="text-left py-2 pr-4 font-semibold text-gray-600 text-xs uppercase tracking-wide">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {wellFormedRows.map((cells, i) => {
                // Collapse any cells beyond the declared column count into the last
                // column, so extra trailing "Control C-XX ... GAP G-XX ..." refs the
                // model appended past the expected field count aren't silently dropped.
                const normalized = cells.length > columns.length
                  ? [...cells.slice(0, columns.length - 1), cells.slice(columns.length - 1).join(' ')]
                  : cells;
                return (
                  <tr key={i} className="border-b border-gray-100 last:border-0 align-top">
                    {columns.map((_, colIndex) => (
                      <td key={colIndex} className={`py-2 pr-4 break-words ${colIndex === 0 ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                        {normalized[colIndex] ? renderTextWithInlineMarkers(normalized[colIndex], registryNums) : ''}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        );
      }
    }

    // Split content into lines based on various delimiters
    const splitIntoLines = (text: string) => {
      // CRITICAL FIX: Convert escaped newlines (\n) to actual newlines first!
      let processedText = text
        // Convert literal \n\n to actual double newlines (for definitions spacing)
        .replace(/\\n\\n/g, '\n\n')
        // Convert single \n to actual newline BUT keep markers intact
        .replace(/\\n(?!\s*[A-Z][^:]+:)/g, '\n')
        // Join broken markers back together (e.g., "[DOCUMENTED:\ntext]" → "[DOCUMENTED: text]")
        .replace(/(\[(?:DOCUMENTED|INFERRED|ASSUMED|GAP IDENTIFIED|CONTROL POINT|SCREENSHOT NEEDED|FLOWCHART NEEDED|CONFLICT|SOURCE|ASSUMPTION):)\s*\n\s*/g, '$1 ');

      // Protect the interior of full "[TAG: ... ]" spans from the sentence-splitter
      // below. A multi-sentence gap/control entry like
      //   "G-01: [GAP IDENTIFIED: First sentence. Second sentence.] (applies to: ...)"
      // would otherwise be split at the "." inside the tag, breaking the opening "["
      // onto one line and leaving a stray "]" on the next — the exact artifact seen
      // in the Gaps Register (Section 12.0). Temporarily replace ". " with a sentinel
      // inside any [TAG: ...] span, split, then restore.
      const SENTENCE_DOT = String.fromCharCode(0xE000); // U+E000 private-use sentinel
      processedText = processedText.replace(
        /\[(?:DOCUMENTED|INFERRED|ASSUMED|GAP IDENTIFIED|CONTROL POINT|SCREENSHOT NEEDED|FLOWCHART NEEDED|CONFLICT|SOURCE|ASSUMPTION|RECOMMENDATION|Conforms to[^\]\[]{0,120}|→\s*[CG]-\d+):[^\]\[]*\]/g,
        (tag) => tag.replace(/\.\s+/g, `.${SENTENCE_DOT} `)
      );

      processedText = processedText
        // CRITICAL: Full stop followed by space and capital letter OR bracket = NEW PARAGRAPH
        // Exclude single-capital-letter abbreviations (initials like "W.", "U.S.",
        // acronym-with-period like "PMP.", "MSPM.") so names like "William W. Davis"
        // and "PMP. Responsible for..." don't get split mid-name/mid-sentence.
        .replace(/(?<!\b[A-Z])\.\s+([A-Z\[])/g, '.\n\n$1')
        // Start a NEW line before an inline numbered-list marker ("...text. 2. Next
        // item") so the marker begins its own line and the merge pass below can join
        // it with its item text on a single line, instead of the number dangling at
        // the end of the previous sentence.
        .replace(/([.:])\s+(\d+\.)\s+([A-Z])/g, '$1\n\n$2 $3')
        // Restore the protected in-tag sentence boundaries.
        .split(SENTENCE_DOT).join('');

      // Then split by actual newlines
      let parts = processedText.split('\n').filter(p => p.trim().length > 0);

      // Merge a numbering-only line (e.g. "5.1", "1.0", or a bare list marker "1.")
      // with no title/item text following it on the same line into the next line, so
      // the section-header regex below can match "number + title" as a single line
      // and render them side by side instead of the number appearing alone with its
      // text falling to the line underneath.
      const NUMBER_ONLY_RE = /^\d+(?:\.\d+)+\.?$/; // section numbers: 5.1, 1.0, 5.1.2
      const LIST_MARKER_ONLY_RE = /^\d+\.$/; // bare numbered-list markers: 1. 2. 3.
      const merged: string[] = [];
      for (let i = 0; i < parts.length; i++) {
        const current = parts[i].trim();
        if (NUMBER_ONLY_RE.test(current) && i + 1 < parts.length) {
          // Section number (5.1, 1.0) — strip trailing "." so it matches the
          // section-header regex "\d+(\.\d+)+" exactly (no trailing dot).
          merged.push(`${current.replace(/\.$/, '')} ${parts[i + 1].trim()}`);
          i++;
        } else if (LIST_MARKER_ONLY_RE.test(current) && i + 1 < parts.length) {
          // Bare numbered-list marker (1., 2.) — keep the trailing "." so it still
          // reads as a numbered list item once merged with its text.
          merged.push(`${current} ${parts[i + 1].trim()}`);
          i++;
        } else {
          merged.push(parts[i]);
        }
      }

      // DON'T split by markers - keep them inline with text!
      return merged;
    };

    const lines = splitIntoLines(filteredContent);

    // Group orphaned lines under the step/appendix header they belong to, so a step's
    // title, "Performed by:" line, and body render as ONE visual block (Asana
    // 1216992325540149, item 2) instead of each sentence becoming an independent,
    // inconsistently-styled paragraph. Also used to gather Appendix flow-summary
    // sentences into one numbered list (item 5). lineOwner[i] = the index of the
    // header line that owns line i, or null if line i renders independently as today.
    const HEADER_LINE_RE = /^\d+(?:\.\d+)+\s+.+$/;
    const APPENDIX_LINE_RE = /^Appendix\s+[A-Z]\s+—/i;
    const lineOwner: (number | null)[] = new Array(lines.length).fill(null);
    {
      let activeOwner: number | null = null;
      for (let i = 0; i < lines.length; i++) {
        const t = lines[i].trim();
        if (HEADER_LINE_RE.test(t)) {
          const numMatch = t.match(/^(\d+(?:\.\d+)+)\s+/);
          const num = numMatch ? numMatch[1] : '';
          const dotCount = (num.match(/\./g) || []).length;
          const isTopLevel = /\.0$/.test(num);
          const isNonLeafSubstep = !isTopLevel && dotCount === 1; // 5.1, 5.2 — not 1.0/5.0, not 5.1.2
          // A references heading has the shape of a procedure sub-step but is a list
          // heading: claiming its entries as step body would route them to the plain
          // paragraph branch, past the bullet/numbered-list branches that bold each
          // reference name.
          activeOwner = isNonLeafSubstep && !REFERENCE_HEADING_RE.test(t) ? i : null;
          continue;
        }
        if (APPENDIX_LINE_RE.test(t)) {
          activeOwner = i;
          continue;
        }
        if (activeOwner !== null) lineOwner[i] = activeOwner;
      }
    }

    // Show empty state if no content matches the filter
    if (lines.length === 0 && viewMode !== 'all') {
      return (
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm">
            {viewMode === 'original'
              ? 'No original content markers found in this section.'
              : 'No AI-added content markers found in this section.'}
          </p>
        </div>
      );
    }

    return (
      <div className="prose prose-gray max-w-none space-y-1">
        {lines.map((line, index) => {
          let trimmedLine = line.trim();

          // Strip an orphan trailing "]" that the model sometimes leaves when it opens
          // a [GAP IDENTIFIED: ...] / [CONTROL POINT: ...] tag but places the closing "]"
          // after a trailing sentence (e.g. "Applies to: Section 1.0, ... scope.]").
          // Only strip when the line has more "]" than "[" so real markers stay intact.
          if (trimmedLine.endsWith(']')) {
            const opens = (trimmedLine.match(/\[/g) || []).length;
            const closes = (trimmedLine.match(/\]/g) || []).length;
            if (closes > opens) trimmedLine = trimmedLine.replace(/\s*\]\s*$/, '');
          }

          if (!trimmedLine) return null;

          // Rendered as part of its owning step/appendix block below — skip here.
          if (lineOwner[index] !== null) return null;

          // PM Step linkage badges are shown in the section header — skip inline markers
          if (trimmedLine.startsWith('__PM_STEP_BADGE__')) return null;

          // Section headers - FIXED: Support multi-level numbering (1.0, 5.1, 5.1.2, 5.1.2.3, etc.)
          const sectionMatch = trimmedLine.match(/^(\d+(?:\.\d+)+)\s+(.+)$/);
          if (sectionMatch) {
            // Clean up the title - remove trailing colons and clean up spacing
            const cleanTitle = sectionMatch[2].replace(/:\s*$/, '').trim();
            const isSubStep = !sectionMatch[1].endsWith('.0');

            if (isSubStep) {
              // Procedure sub-steps (5.1, 5.2, 5.1.1 etc.) — purple accent.
              // Two distinct shapes reach this branch:
              //  (a) Top-level steps (5.1, 5.2 — exactly one dot) are written as
              //      "<short title>: <full body sentence(s)>" — a short label before
              //      the first colon, then the complete step description after it.
              //  (b) Leaf sub-steps (5.2.1, 5.3.4 — two or more dots) are usually a
              //      single short instruction with NO title/body colon at all (e.g.
              //      "Confirm that the uncertainty is suited to bell-shaped modeling.").
              //      These must render as a single plain-text line — bold must never
              //      wrap the entire sentence just because no colon was found to split on.
              const dotCount = (sectionMatch[1].match(/\./g) || []).length;
              const isLeafSubStep = dotCount >= 2;

              // Detect a stray "]" with no matching "[" — a bracket-tag artifact left
              // over from step-title generation, e.g. "Start: Requestor]: The process...".
              // When present, the pattern is "<Title>: <Actor>]: <Body>" — two colons
              // before the real body starts, with the actor name sandwiched between
              // them. Fold the actor into the title and split at the SECOND colon.
              // Without the artifact, the pattern is just "<Title>: <Body>" — split
              // at the first (only) colon.
              let titleSource = cleanTitle;
              const hasStrayBracket = (titleSource.match(/\]/g) || []).length > (titleSource.match(/\[/g) || []).length;
              if (hasStrayBracket) titleSource = titleSource.replace(/\]/g, '');

              // Extract "(Performed by: <role>)" before the title/body colon split runs.
              // The role clause is written as "<Title> (Performed by: <Role>): <Body>" —
              // since the clause itself contains a colon right after "Performed by", the
              // naive first-colon split below would cut the title off mid-parenthetical
              // ("...( Performed by") and spill the rest ("Role): Body...") into the body.
              // Role names can themselves contain one level of parens (e.g. "Employee
              // Relations (ER) COE"), so match up to one nested paren pair. An optional
              // "[DECISION POINT]" suffix (already bracket-stripped above if malformed)
              // can appear between the closing paren and the real colon.
              const performedByMatch = titleSource.match(/^(.*?)\s*\(Performed by:\s*([^()]*(?:\([^()]*\)[^()]*)*)\)(\s*\[DECISION POINT\])?:\s*/i);
              let performedByRole: string | null = null;
              let decisionPointSuffix = '';
              if (performedByMatch) {
                titleSource = `${performedByMatch[1]}: ${titleSource.slice(performedByMatch[0].length)}`;
                performedByRole = performedByMatch[2].trim();
                decisionPointSuffix = performedByMatch[3] ? ' [DECISION POINT]' : '';
              }

              let stepTitle: string;
              let stepBody: string;
              if (hasStrayBracket) {
                const twoColonSplit = titleSource.match(/^([^:]{1,100}):\s*([^:]{1,60}):\s*([\s\S]*)$/);
                if (twoColonSplit) {
                  stepTitle = `${twoColonSplit[1].trim()} — ${twoColonSplit[2].trim()}`;
                  stepBody = twoColonSplit[3].trim();
                } else {
                  const fallback = titleSource.match(/^([^:]{1,100}):\s*([\s\S]*)$/);
                  stepTitle = (fallback ? fallback[1] : titleSource).trim();
                  stepBody = fallback ? fallback[2].trim() : '';
                }
              } else {
                const titleSplit = titleSource.match(/^([^:]{1,100}):\s*([\s\S]*)$/);
                stepTitle = (titleSplit ? titleSplit[1] : titleSource).trim();
                stepBody = titleSplit ? titleSplit[2].trim() : '';
              }
              if (performedByRole) stepTitle = `${stepTitle}${decisionPointSuffix} (Performed by: ${performedByRole})`;

              // Leaf sub-steps with no real title/body split (no colon found, so the
              // whole sentence fell into stepTitle) render as plain text — no bold.
              // Use stepTitle (not titleSource) when a "(Performed by:...)" clause was
              // extracted above — titleSource has a synthetic ": " where that clause
              // used to be, while stepTitle already has it correctly reattached.
              if (isLeafSubStep && !stepBody) {
                return (
                  <div key={index} className="mt-2 mb-1">
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-semibold text-purple-600 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded mt-0.5 whitespace-nowrap">
                        {sectionMatch[1]}
                      </span>
                      <span className="text-sm font-normal text-gray-700 leading-relaxed">
                        {renderTextWithInlineMarkers(performedByRole ? stepTitle : titleSource, registryNums)}
                      </span>
                    </div>
                  </div>
                );
              }

              // Gather every line the ownership pre-pass assigned to this step header —
              // the rest of the step's body, which the backend often writes as separate
              // sentences (e.g. a standalone "Performed by: <Role>." sentence that
              // doesn't match the "(Performed by: ...)" parenthetical shape above).
              // Pulling "Performed by:" out of here (when the parenthetical didn't
              // already supply it) and rendering every owned line inside the SAME
              // block — same font size, grouped together, with clear space before the
              // next step — fixes the "random gap + differently-sized Performed by
              // line" symptom (Asana 1216992325540149, item 2).
              const ownedBodyLines = lines
                .map((l, i) => (lineOwner[i] === index ? l.trim() : null))
                .filter((l): l is string => !!l);

              let mergedPerformedByRole = performedByRole;
              const extraBodyLines: string[] = [];
              for (const ownedLine of ownedBodyLines) {
                const pbMatch = !mergedPerformedByRole && ownedLine.match(/^performed by:\s*([^.]+)\.?\s*$/i);
                if (pbMatch) {
                  mergedPerformedByRole = pbMatch[1].trim();
                } else {
                  extraBodyLines.push(ownedLine);
                }
              }
              const displayTitle = (mergedPerformedByRole && !performedByRole)
                ? `${stepTitle} (Performed by: ${mergedPerformedByRole})`
                : stepTitle;

              return (
                <div key={index} className="mt-4 mb-4">
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-purple-600 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded mt-0.5 whitespace-nowrap">
                      {sectionMatch[1]}
                    </span>
                    <div className="flex-1 space-y-1">
                      <span className="text-sm font-semibold text-purple-800 leading-snug block">
                        {displayTitle}
                      </span>
                      {stepBody && (
                        <p className="text-sm font-normal text-gray-700 leading-relaxed">
                          {renderTextWithInlineMarkers(stepBody, registryNums)}
                        </p>
                      )}
                      {extraBodyLines.map((l, li) => (
                        <p key={li} className="text-sm font-normal text-gray-700 leading-relaxed">
                          {renderTextWithInlineMarkers(l, registryNums)}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div key={index} className="mt-6 mb-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-gray-900 w-8 h-8 rounded-full flex items-center justify-center text-md p-2">
                    {sectionMatch[1]}
                  </span>
                  <h2 className="text-sm leading-relaxed text-gray-700 m-0">
                    {cleanTitle}
                  </h2>
                </div>
              </div>
            );
          }

          // IMPROVED: Handle role definitions - FIXED: Support multi-level numbering and colon formatting
          const roleMatch = trimmedLine.match(/^(\d+(?:\.\d+)+)\s+(.+)$/);
          if (roleMatch && !sectionMatch) {
            const fullNumber = roleMatch[1]; 
            const restOfLine = roleMatch[2];

            
            const numberParts = fullNumber.split('.');
            const badgeNumber = numberParts.length >= 2
              ? numberParts.slice(-2).join('.')
              : fullNumber;

            return (
              <div key={index} className="mt-4 mb-2">
                <div className="flex items-start gap-3">
                  <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mt-0.5 flex-shrink-0">
                    {badgeNumber}
                  </span>
                  <div className="flex-1">
                    <span className="text-gray-700 leading-relaxed">
                      <strong className="font-semibold">{fullNumber}</strong> {restOfLine}
                    </span>
                  </div>
                </div>
              </div>
            );
          }

          // Appendix flow-summary heading (e.g. "Appendix A — Process Flow Summary: ...")
          // in Section 12.0. Previously rendered as a dense prose wall — now rendered as
          // a numbered, indented list of flow steps, using the lines the ownership
          // pre-pass gathered under this heading as the list items (Asana
          // 1216992325540149, item 5). A short pointer to the process map export is
          // added so the appendix and the map read as complementary, not duplicative.
          if (APPENDIX_LINE_RE.test(trimmedLine)) {
            const headingMatch = trimmedLine.match(/^(Appendix\s+[A-Z]\s+—\s+[^:]+:?)([\s\S]*)$/i);
            const heading = headingMatch ? headingMatch[1] : trimmedLine;
            const inlineRemainder = headingMatch ? headingMatch[2].trim() : '';
            const ownedFlowLines = lines
              .map((l, i) => (lineOwner[i] === index ? l.trim() : null))
              .filter((l): l is string => !!l);
            const flowItems = [inlineRemainder, ...ownedFlowLines].filter(Boolean);

            return (
              <div key={index} className="mt-6 mb-4">
                <strong className="font-semibold text-gray-900">{heading}</strong>
                <p className="text-xs italic text-gray-500 mt-1 mb-2">
                  See the process map export for the same flow as a diagram.
                </p>
                {flowItems.length > 0 && (
                  <ol className="list-none space-y-2 mt-2">
                    {flowItems.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-2 ml-2">
                        <span className="text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          {itemIndex + 1}
                        </span>
                        <span className="text-sm text-gray-700 leading-relaxed">{renderTextWithInlineMarkers(item, registryNums)}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            );
          }

          // Bold-labeled line (e.g. "**Input Required:** description text") — let
          // renderTextWithInlineMarkers handle the ** pairs so the label renders bold
          // and the value after it renders normal, instead of stripping bold entirely.
          if (trimmedLine.startsWith('**') && trimmedLine.includes('**')) {
            return (
              <div key={index} className="text-gray-900 mt-3 mb-1">
                {renderTextWithInlineMarkers(trimmedLine, registryNums)}
              </div>
            );
          }

          // Bullet points
          if (trimmedLine.startsWith('- ')) {
            const bulletText = trimmedLine.substring(2);
            // 11.1 CONFIRMED REFERENCES entries are written as "<Document/Standard
            // Name> — <description>" — bold the name so each reference is easy to
            // scan. "CONFIRMED REFERENCES" is a sub-heading INSIDE Section 11.0's
            // content (sectionTitle is the outer section's title, "Related Policies
            // and Supporting Documents" — it never contains this text), so scope it
            // by scanning backward from this line.
            return (
              <div key={index} className="flex items-start gap-2 ml-4 mb-1">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-gray-700">
                  {referenceListKind(lines, index) === 'confirmed'
                    ? renderReferenceEntry(bulletText, registryNums)
                    : renderTextWithInlineMarkers(bulletText, registryNums)}
                </span>
              </div>
            );
          }

          // Bare numbered-list item (e.g. "1. Distribute the template to all
          // Estimators.") — a single-level list marker, distinct from multi-level
          // section numbers (5.1, 5.1.2) which are handled by sectionMatch above.
          // Rendered as a numbered list row so items visually line up with their number.
          const listItemMatch = trimmedLine.match(/^(\d+)\.\s+(.+)$/);
          if (listItemMatch && !/^\d+\.\d/.test(trimmedLine)) {
            const [, num, itemText] = listItemMatch;
            return (
              <div key={index} className="flex items-start gap-2 ml-1 mb-1">
                <span className="text-gray-500 font-medium flex-shrink-0">{num}.</span>
                <span className="text-gray-700 leading-relaxed">
                  {referenceListKind(lines, index) === 'confirmed'
                    ? renderReferenceEntry(itemText, registryNums)
                    : renderTextWithInlineMarkers(itemText, registryNums)}
                </span>
              </div>
            );
          }

          // Block-level Control Point (standalone)
          if (trimmedLine.match(/^\[CONTROL POINT:/)) {
            const match = trimmedLine.match(/\[CONTROL POINT:\s*([^\]]+)\]/);
            if (match) {
              return (
                <div key={index} className="bg-yellow-50 border-l-4 border-yellow-400 p-3 my-2 rounded-r-md">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🔍</span>
                    <span className="text-yellow-800 font-medium">Control Point: {match[1]}</span>
                  </div>
                </div>
              );
            }
          }
          
          // Block-level Gap Identified (standalone)
          if (trimmedLine.match(/^\[GAP IDENTIFIED:/)) {
            const match = trimmedLine.match(/\[GAP IDENTIFIED:\s*([^\]]+)\]/);
            if (match) {
              return (
                <div key={index} className="bg-red-50 border-l-4 border-red-400 p-3 my-2 rounded-r-md">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">⚠️</span>
                    <span className="text-red-800 font-medium">Gap: {match[1]}</span>
                  </div>
                </div>
              );
            }
          }
          
          // Block-level Screenshot Needed (standalone)
          if (trimmedLine.match(/^\[SCREENSHOT NEEDED:/)) {
            const match = trimmedLine.match(/\[SCREENSHOT NEEDED:\s*([^\]]+)\]/);
            if (match) {
              return (
                <div key={index} className="bg-blue-50 border-l-4 border-blue-400 p-3 my-2 rounded-r-md">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📸</span>
                    <span className="text-blue-800 font-medium">Screenshot: {match[1]}</span>
                  </div>
                </div>
              );
            }
          }
          
          // Block-level Flowchart Needed (standalone)
          if (trimmedLine.match(/^\[FLOWCHART NEEDED:/)) {
            const match = trimmedLine.match(/\[FLOWCHART NEEDED:\s*([^\]]+)\]/);
            if (match) {
              return (
                <div key={index} className="bg-green-50 border-l-4 border-green-400 p-3 my-2 rounded-r-md">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📊</span>
                    <span className="text-green-800 font-medium">Flowchart: {match[1]}</span>
                  </div>
                </div>
              );
            }
          }
          
          // Block-level Assumption (standalone)
          if (trimmedLine.match(/^\[ASSUMPTION:/)) {
            const match = trimmedLine.match(/\[ASSUMPTION:\s*([^\]]+)\]/);
            if (match) {
              return (
                <div key={index} className="bg-purple-50 border-l-4 border-purple-400 p-3 my-2 rounded-r-md">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">💡</span>
                    <span className="text-purple-800 font-medium">Assumption: {match[1]}</span>
                  </div>
                </div>
              );
            }
          }
          
          // Block-level Source (standalone) — like the inline SOURCE handler above,
          // this previously rendered a "✓ Original: <filename>" green box, but the
          // filename it names is already stated earlier in the same Section 11.1
          // bullet, making it a redundant repeat. Render nothing.
          if (trimmedLine.match(/^\[SOURCE:/)) {
            const match = trimmedLine.match(/\[SOURCE:\s*([^\]]+)\]/);
            if (match) return null;
          }
          
          // Block-level Conflict (standalone)
          if (trimmedLine.match(/^\[CONFLICT:/)) {
            const match = trimmedLine.match(/\[CONFLICT:\s*([^\]]+)\]/);
            if (match) {
              return (
                <div key={index} className="bg-orange-50 border-l-4 border-orange-400 p-3 my-2 rounded-r-md">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">⚡</span>
                    <span className="text-orange-800 font-medium">Conflict: {match[1]}</span>
                  </div>
                </div>
              );
            }
          }
          
    
          const isNumberedLine = /^\d+\.\d+/.test(trimmedLine);

          // Semicolon-list-to-bullets: a single paragraph containing 4+ semicolon-
          // separated items reads as a wall of text and is hard to scan. Convert it
          // to a labeled bullet list instead. Does NOT apply to CONTROL POINT / GAP
          // IDENTIFIED callouts or bracket-marker lines (those return earlier above,
          // or start with "[" and are excluded below) — only plain narrative paragraphs.
          const semicolonList = !isNumberedLine ? parseSemicolonList(trimmedLine) : null;
          if (semicolonList) {
            return (
              <div key={index} className="mt-3 mb-2">
                {semicolonList.label && (
                  <p className="text-gray-900 font-semibold mb-1">{semicolonList.label}:</p>
                )}
                {!semicolonList.label && semicolonList.intro && (
                  <p className="text-gray-700 leading-relaxed mb-1">{semicolonList.intro}</p>
                )}
                <ul className="list-none space-y-1">
                  {semicolonList.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-2 ml-1">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0" />
                      <span className="text-gray-700 leading-relaxed">{renderTextWithInlineMarkers(item, registryNums)}</span>
                    </li>
                  ))}
                </ul>
                {semicolonList.trailing && (
                  <p className="text-gray-700 leading-relaxed mt-1">{renderTextWithInlineMarkers(semicolonList.trailing, registryNums)}</p>
                )}
              </div>
            );
          }

          // Label-style header followed by a colon (e.g. "In Scope:", "Out of Scope:",
          // "Geographic and Organizational Scope:") — bold the label, leave the rest normal.
          // Excludes lines that are just markers ([SOURCE: ...]) or already-numbered lines.
          const colonHeaderMatch = trimmedLine.match(/^([A-Z][A-Za-z0-9 /&\-]{1,60}):\s*(.*)$/);
          if (colonHeaderMatch && !trimmedLine.startsWith('[') && !isNumberedLine) {
            const [, label, rest] = colonHeaderMatch;
            return (
              <div key={index} className="text-gray-700 leading-relaxed mb-3">
                <strong className="font-semibold text-gray-900">{label}:</strong>{rest ? ' ' : ''}{renderTextWithInlineMarkers(rest, registryNums)}
              </div>
            );
          }

          // A line inside a reference list is one of two things: an entry the model
          // wrote with no bullet and no list number, or the sentence continuing the
          // entry above it ("Applied throughout this SOP in controls C-01 through
          // C-13.", "Not confirmed in source — validate with compliance team."). A
          // continuation is indented to the list's text column so it reads as part of
          // its entry instead of starting a new paragraph back at the section margin.
          // Entries are told apart by the [SOURCE: ...] citation every confirmed
          // reference must carry, so a plain sentence is never bolded as a name.
          const referenceList = referenceListKind(lines, index);
          if (referenceList) {
            const isEntry = referenceList === 'confirmed' && /\[SOURCE:/i.test(trimmedLine);
            return (
              <div
                key={index}
                className={`text-gray-700 leading-relaxed ${isEntry ? 'ml-1 mb-1' : 'ml-6 mb-2'}`}
              >
                {isEntry ? renderReferenceEntry(trimmedLine, registryNums) : renderTextWithInlineMarkers(trimmedLine, registryNums)}
              </div>
            );
          }

          // Plain narrative paragraph (no bullet/marker/label) — visible spacing here is
          // what separates "Overview and Purpose"-style running text into readable
          // paragraph chunks instead of one dense block (Asana 1216992325540149, item 1).
          return (
            <div key={index} className="text-gray-700 leading-relaxed mb-3">
              {renderTextWithInlineMarkers(trimmedLine, registryNums)}
            </div>
          );
        })}
      </div>
    );
};

export default SOPContentRenderer;